/**
 * App Settings — three sub-tabs (Uplift Coeff + Goals / Rules for
 * Goals / Rules for New Campaigns). Phase 2 ships the first two
 * end-to-end (CSV ingest + computed coefficients table + 3-card
 * goal rules). New Campaigns is still placeholder for v1.2.
 */

import { useEffect, useMemo, useState } from "react";
import { Btn } from "@/components/Btn";
import { I } from "@/components/Icon";
import { OverviewTilesEditor } from "@/components/settings/OverviewTilesEditor";
import {
  useApps,
  useAppCohortConfig,
  useAppGoalTarget,
  useDeleteSegmentGoal,
  useGoalRules,
  useSegmentGoals,
  useUpdateGoalRule,
  useUpliftCoefficients,
  useUpliftData,
  useUploadUpliftData,
  useUpsertAppCohortConfig,
  useUpsertAppGoalTarget,
  useUpsertSegmentGoal,
  type CohortSource,
  type GoalMetric,
  type UpliftUploadRow,
} from "@/lib/api-hooks";
import { useLoadedCoefficients } from "@/lib/ltv-hooks";
import { StSectionHead } from "./_shared";
import { useStickyAppId } from "./useStickyAppId";
import {
  CommissionSbpSection,
  CustomMetricsSection,
  LtvAdvancedSection,
  LtvCoefficientsSection,
  LtvEventMappingSection,
  SubscriptionPricesSection,
  SubscriptionsSection,
} from "./LtvSections";

type SubId =
  | "coeff-goals"
  | "custom-metrics"
  | "ltv-advanced"
  | "subs"
  | "sub-prices"
  | "commission"
  | "ltv-events"
  | "data-provider"
  | "tiles"
  | "goals"
  | "newcamp";

// Grouped sub-nav per the App Settings design §1 — sectioned, not a flat list.
type SubGroup = { header: string; items: { id: SubId; label: string }[] };

const SUB_GROUPS: SubGroup[] = [
  {
    header: "Coefficients & Goals",
    items: [
      { id: "coeff-goals", label: "Coefficients & Goals" },
      { id: "custom-metrics", label: "Custom metrics" },
      { id: "ltv-advanced", label: "Advanced" },
    ],
  },
  {
    header: "Subscriptions",
    items: [
      { id: "subs", label: "Subscriptions" },
      { id: "sub-prices", label: "Subscription prices" },
      { id: "commission", label: "Commission / SBP" },
    ],
  },
  {
    header: "Data",
    items: [
      { id: "ltv-events", label: "Event mapping" },
      { id: "data-provider", label: "Data provider" },
      { id: "tiles", label: "Overview tiles" },
    ],
  },
  {
    header: "Rules",
    items: [
      { id: "goals", label: "Rules for Goals" },
      { id: "newcamp", label: "Rules for New Campaigns" },
    ],
  },
];

// The 10 Adjust events trackable for the Adjust cohort source.
const ADJUST_EVENT_OPTIONS = [
  "MobilePurchase",
  "rv_NonSubscriptionPurchase",
  "rv_ProductChange",
  "rv_SubscriptionCancel",
  "rv_SubscriptionExpiration",
  "rv_SubscriptionInitialPurchase",
  "rv_SubscriptionRenewal",
  "rv_SubscriptionTrialActivate",
  "rv_SubscriptionTrialCancel",
  "rv_SubscriptionTrialStart",
];

export function AppSettingsTab() {
  const [sub, setSub] = useState<SubId>("coeff-goals");
  const groups = SUB_GROUPS;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "220px minmax(0, 1fr)",
        gap: 24,
        alignItems: "start",
      }}
    >
      <nav className="card" style={{ padding: 8 }}>
        {groups.map((g) => (
          <div key={g.header}>
            <div
              className="muted"
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                padding: "12px 10px 4px",
              }}
            >
              {g.header}
            </div>
            {g.items.map((s) => (
              <div
                key={s.id}
                className={`nav-item ${sub === s.id ? "active" : ""}`}
                onClick={() => setSub(s.id)}
                style={{ cursor: "pointer" }}
              >
                <span>{s.label}</span>
              </div>
            ))}
          </div>
        ))}
      </nav>
      <div>
        {sub === "coeff-goals" && <CoefficientsAndGoalsSection />}
        {sub === "custom-metrics" && <CustomMetricsSection />}
        {sub === "ltv-advanced" && <LtvAdvancedSection />}
        {sub === "subs" && <SubscriptionsSection />}
        {sub === "sub-prices" && <SubscriptionPricesSection />}
        {sub === "commission" && <CommissionSbpSection />}
        {sub === "ltv-events" && <LtvEventMappingSection />}
        {sub === "data-provider" && <CohortConfigSection />}
        {sub === "tiles" && <OverviewTilesSection />}
        {sub === "goals" && <GoalRulesSection />}
        {sub === "newcamp" && <NewCampaignsPlaceholder />}
      </div>
    </div>
  );
}

// Coefficients & Goals — the merged screen (design §4): the uplift/goals table
// plus the three-row Loaded / Factual / Predict coefficients, driven by one
// shared sticky app picker (UpliftSection owns the picker; the coefficients
// block follows the same app live via the reactive useStickyAppId).
function CoefficientsAndGoalsSection() {
  const apps = useApps();
  const [appId] = useStickyAppId(apps.data);
  return (
    <>
      <UpliftSection />
      {appId != null && (
        <div style={{ marginTop: 28 }}>
          <LtvCoefficientsSection controlledAppId={appId} />
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// 6.1 Uplift Coeff + Goals
// ---------------------------------------------------------------------------

type UpliftPredict = { from: number; to: number };

// Day-anchor options for the "for" picker. Mirrors the cohort grid
// (keyword_cohort_revenue) plus D0 — `org_uplift_data` rows can
// land at any of these positions and `org_app_goal_target.goal_day`
// CHECK allows 0..730.
const GOAL_DAY_OPTIONS = [
  0, 1, 3, 4, 7, 10, 14, 15, 20, 30, 35, 60, 90, 180,
];
const GOAL_METRIC_OPTIONS: GoalMetric[] = ["ARPU", "ARPPU", "ROAS"];

// Column-header bulk-edit affordance for Goal 1/2/3. Click the
// header → input flips visible → type → Enter / blur commits the
// value to every row in the table for that goal_n via onApply().
// Per-cell editing in the body still works independently (last
// write wins). Mirrors the design call-out: "тык в Goal N меняет
// весь столбец, отдельные клетки можно переопределить вручную".
function BulkGoalHeader({
  n,
  firstInGroup,
  busy,
  metric,
  onApply,
}: {
  n: 1 | 2 | 3;
  firstInGroup: boolean;
  busy: boolean;
  // Optional metric label shown beside "Goal N" so the user knows
  // what unit the column's numbers are in at a glance.
  metric?: GoalMetric;
  onApply: (raw: string) => void | Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed !== "" && Number.isFinite(Number(trimmed))) {
      void onApply(trimmed);
    }
    setEditing(false);
    setDraft("");
  };

  return (
    <th
      className="num"
      style={{
        whiteSpace: "nowrap",
        borderLeft: firstInGroup ? "1px solid var(--c-divider)" : undefined,
        cursor: editing ? "default" : "pointer",
        background: editing ? "var(--c-accent-50)" : undefined,
        // Hairline hint that the header is clickable; matches the
        // dashed underline pattern used elsewhere for "click-to-act".
        textDecoration: editing ? "none" : "underline dotted",
        textUnderlineOffset: 4,
      }}
      onClick={() => {
        if (!editing && !busy) setEditing(true);
      }}
      title={editing ? "" : `Click to set Goal ${n} for every row`}
    >
      {editing ? (
        <input
          className="input mono"
          autoFocus
          type="number"
          step="0.01"
          min="0"
          placeholder={`Goal ${n} for all`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") {
              setEditing(false);
              setDraft("");
            }
          }}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: 90,
            textAlign: "right",
            fontSize: 12,
            padding: "4px 6px",
          }}
        />
      ) : (
        <>
          Goal {n}
          {metric && (
            <>
              <br />
              <span
                className="muted"
                style={{ fontWeight: 400, fontSize: 11 }}
              >
                {metric}
              </span>
            </>
          )}
        </>
      )}
    </th>
  );
}

// Reusable picker block — appears once per app in both legacy
// (raw-ARPU upload) and stored-only modes. Border colour mirrors
// "Rules for Goals" cards: orange = goal_target.configured_at IS
// NULL, green = stamped.
function GoalTargetPickerBlock({ appId }: { appId: number }) {
  const q = useAppGoalTarget(appId);
  const coeffs = useLoadedCoefficients(appId);
  const upsert = useUpsertAppGoalTarget();

  // Local draft, hydrated from server. Lets the user pick a value
  // without firing a mutation per keystroke; commit on Apply.
  const [metric, setMetric] = useState<GoalMetric>("ARPU");
  const [day, setDay] = useState<number>(30);

  // The "for" horizons = the static cohort-grid anchors PLUS the distinct
  // to_day of the app's loaded RENEWAL coefficients (365 / 545 for Grids).
  // Those far horizons are exactly the goal_days the predict path can reach
  // and that completeness accepts (it checks a loaded coefficient at
  // anchor→goal_day). The static list is kept so a day WITHOUT coefficients
  // (e.g. D30) stays selectable — completeness then correctly blocks it. A ✓
  // marks a day backed by a loaded coefficient; `day` is merged so a
  // server-set value always renders.
  const loadedGoalDays = useMemo(
    () =>
      new Set(
        (coeffs.data?.rows ?? [])
          .filter((r) => r.coefficient_type === "renewal")
          .map((r) => r.to_day),
      ),
    [coeffs.data],
  );
  const dayOptions = useMemo(
    () =>
      [...new Set<number>([...GOAL_DAY_OPTIONS, ...loadedGoalDays, day])].sort(
        (a, b) => a - b,
      ),
    [loadedGoalDays, day],
  );
  useEffect(() => {
    if (q.data) {
      setMetric(q.data.goal_metric);
      setDay(q.data.goal_day);
    }
  }, [q.data]);

  const configured = q.data?.configured_at != null;
  // After Apply, if the user changes the dropdown the border should
  // immediately go orange (draft diverged from confirmed value).
  const draftMatchesConfirmed =
    configured &&
    q.data!.goal_metric === metric &&
    q.data!.goal_day === day;
  const showGreen = configured && draftMatchesConfirmed;

  return (
    <div
      className="card"
      style={{
        marginBottom: 16,
        background: showGreen
          ? "linear-gradient(180deg, #ecfaf0, #fff)"
          : "linear-gradient(180deg, #fff8e6, #fff)",
        border: showGreen ? "1px solid #b9e6c5" : "1px solid #f0d894",
      }}
    >
      <div
        style={{
          padding: "14px 20px",
          display: "flex",
          gap: 14,
          alignItems: "end",
          flexWrap: "wrap",
        }}
      >
        <div style={{ minWidth: 140 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Goal target</div>
          <div
            style={{
              fontSize: 11,
              color: "var(--c-text-3)",
              marginTop: 2,
            }}
          >
            {showGreen ? "Configured" : "Not configured yet"}
          </div>
        </div>
        <div className="field" style={{ minWidth: 0, marginBottom: 0 }}>
          <label className="label">My goal is</label>
          <select
            className="select"
            value={metric}
            onChange={(e) => setMetric(e.target.value as GoalMetric)}
          >
            {GOAL_METRIC_OPTIONS.map((m) => (
              <option key={m}>{m}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ minWidth: 0, marginBottom: 0 }}>
          <label className="label">for</label>
          <select
            className="select"
            value={day}
            onChange={(e) => setDay(Number(e.target.value))}
          >
            {dayOptions.map((d) => (
              <option key={d} value={d}>
                D{d}
                {loadedGoalDays.has(d) ? " ✓" : ""}
              </option>
            ))}
          </select>
        </div>
        <div style={{ marginLeft: "auto" }}>
          {showGreen ? (
            <Btn
              icon={I.edit}
              onClick={() =>
                void upsert.mutateAsync({
                  app_id: appId,
                  goal_metric: metric,
                  goal_day: day,
                  confirmed: false,
                })
              }
            >
              Edit
            </Btn>
          ) : (
            <Btn
              variant="primary"
              onClick={() =>
                void upsert.mutateAsync({
                  app_id: appId,
                  goal_metric: metric,
                  goal_day: day,
                  confirmed: true,
                })
              }
            >
              Apply
            </Btn>
          )}
        </div>
      </div>
    </div>
  );
}

// Goal VALUES per segment — where a detection-ON (LTV-coefficient) app enters
// its Goal 1/2/3 targets. Legacy / uplift apps enter these in the Uplift
// table's Goal columns, but a detection-ON app (Grids) has an empty Uplift
// table, so it gets this dedicated block. Backed by org_app_segment_goals —
// the 3-layer target the predict path reads (campaign → country → global).
const _SEG_GOAL_NS: (1 | 2 | 3)[] = [1, 2, 3];

function SegmentGoalsBlock({ appId }: { appId: number }) {
  const coeffs = useLoadedCoefficients(appId);
  const goalTargetQ = useAppGoalTarget(appId);
  const goalsQ = useSegmentGoals(appId);
  const upsert = useUpsertSegmentGoal();
  const del = useDeleteSegmentGoal();
  const [newCountry, setNewCountry] = useState("");
  const [extraCountries, setExtraCountries] = useState<string[]>([]);

  const goals = useMemo(() => goalsQ.data ?? [], [goalsQ.data]);
  const metric = goalTargetQ.data?.goal_metric;
  const countries = useMemo(() => {
    const s = new Set<string>(extraCountries);
    for (const g of goals) {
      if (g.country && !g.campaign) s.add(g.country.toUpperCase());
    }
    return [...s].sort();
  }, [goals, extraCountries]);

  // Detection-ON discriminator: an app with loaded coefficients uses this
  // block; a legacy uplift app (no loaded coeffs) enters goals in its table.
  const isLtvApp = (coeffs.data?.rows.length ?? 0) > 0;
  if (!isLtvApp) return null;

  const valueOf = (country: string | null, n: number): string => {
    const g = goals.find(
      (x) => (x.country ?? null) === country && !x.campaign && x.goal_n === n,
    );
    return g?.target_value != null ? String(g.target_value) : "";
  };
  const setValue = (country: string | null, n: number, raw: string) => {
    const v = raw.trim();
    if (v === "" || !Number.isFinite(Number(v))) {
      void del.mutateAsync({ app_id: appId, country, campaign: null, goal_n: n });
      return;
    }
    void upsert.mutateAsync({
      app_id: appId, country, campaign: null, goal_n: n, target_value: Number(v),
    });
  };
  const addCountry = () => {
    const c = newCountry.trim().toUpperCase();
    if (c && !countries.includes(c)) setExtraCountries((p) => [...p, c]);
    setNewCountry("");
  };

  const rows: (string | null)[] = [null, ...countries];

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ padding: "14px 20px" }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>Goal values · per segment</div>
        <div style={{ fontSize: 11, color: "var(--c-text-3)", margin: "2px 0 10px" }}>
          The target the autopilot steers each segment to
          {metric ? ` — in ${metric}` : " — set the unit in “Goal target” above"}.
          Looked up campaign → country → Global; the Global row is the fallback.
        </div>
        <div className="table-scroll">
          <table className="dt" style={{ minWidth: 0 }}>
            <thead>
              <tr>
                <th>Segment</th>
                {_SEG_GOAL_NS.map((n) => (
                  <th key={n} className="num">Goal {n}</th>
                ))}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((country) => (
                <tr key={country ?? "__global__"}>
                  <td>
                    {country === null ? (
                      <b>Global (default)</b>
                    ) : (
                      <span style={{ fontWeight: 600 }}>{country}</span>
                    )}
                  </td>
                  {_SEG_GOAL_NS.map((n, i) => (
                    <GoalCpaCell
                      key={n}
                      firstInGroup={i === 0}
                      metric={metric}
                      value={valueOf(country, n)}
                      onChange={(next) => setValue(country, n, next)}
                    />
                  ))}
                  <td className="num">
                    {country !== null && (
                      <button
                        className="btn icon"
                        title="Remove country (clears its goals)"
                        onClick={() => {
                          _SEG_GOAL_NS.forEach((n) =>
                            void del.mutateAsync({
                              app_id: appId, country, campaign: null, goal_n: n,
                            }),
                          );
                          setExtraCountries((p) => p.filter((x) => x !== country));
                        }}
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
          <input
            className="input"
            placeholder="Country ISO2 (e.g. US)"
            value={newCountry}
            maxLength={2}
            onChange={(e) => setNewCountry(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") addCountry();
            }}
            style={{ width: 180 }}
          />
          <Btn onClick={addCountry}>Add country</Btn>
        </div>
      </div>
    </div>
  );
}

function UpliftSection() {
  const apps = useApps();
  const [appId, setAppId] = useStickyAppId(apps.data);
  const [setupOpen, setSetupOpen] = useState(true);
  const [csvText, setCsvText] = useState("");
  const [csvError, setCsvError] = useState<string | null>(null);
  const [parsedRows, setParsedRows] = useState<UpliftUploadRow[]>([]);

  // Setup form values per handoff. mainFrom/mainTo + predicts feed
  // the column derivation logic on the right table; splitBy controls
  // which extra column (Keyword vs Campaign) the table grows; dataKind
  // labels the per-day numeric columns ARPU vs ARPPU. dataKind also
  // gets passed through on every CSV row.
  const [mainFrom, setMainFrom] = useState(0);
  const [mainTo, setMainTo] = useState(4);
  const [splitBy, setSplitBy] = useState<
    "Country" | "Country + Keyword" | "Campaign Name"
  >("Country + Keyword");
  const [dataKind, setDataKind] = useState<"ARPU" | "ARPPU">("ARPU");
  const [predicts, setPredicts] = useState<UpliftPredict[]>([
    { from: 4, to: 30 },
    { from: 30, to: 90 },
  ]);

  const upload = useUploadUpliftData();
  const dataQuery = useUpliftData(appId);
  const coeffsQuery = useUpliftCoefficients(appId);

  // Per-segment Goal cells. Hydrate from /segment-goals (table
  // org_app_segment_goals, migration 0055); on edit, PUT one cell
  // at a time and let TanStack invalidate to re-fetch. We keep a
  // local-draft layer on top so the user's typing-in-progress isn't
  // overwritten by a mid-request refetch.
  const segmentGoalsQuery = useSegmentGoals(appId);
  const upsertSegmentGoal = useUpsertSegmentGoal();
  // Per-app goal target unit — drives Goal column header label
  // ("Goal 1 / ROAS") and whether GoalCpaCell renders the `$`
  // prefix (ARPU/ARPPU only — ROAS is a multiplier).
  const goalTargetQuery = useAppGoalTarget(appId);
  const goalMetric: GoalMetric | undefined =
    goalTargetQuery.data?.goal_metric;

  // segKey = "country|keyword|campaign". Stored-only rows use
  // keyword="" (never per-keyword); legacy rows preserve the
  // keyword leg from the upload.
  const goalCells = useMemo(() => {
    const out: Record<string, { 1?: string; 2?: string; 3?: string }> = {};
    for (const r of segmentGoalsQuery.data ?? []) {
      // Table render segKey is "country|keyword|campaign". Backend
      // has no keyword column → keyword leg is always "".
      const k = [r.country ?? "", "", r.campaign ?? ""].join("|");
      if (!out[k]) out[k] = {};
      out[k][r.goal_n as 1 | 2 | 3] = String(r.target_value);
    }
    return out;
  }, [segmentGoalsQuery.data]);

  // Wrapper matches the (segKey -> {1,2,3}) shape the table cells
  // already use. Decomposes segKey back to (country, campaign) for
  // the PUT body. NULL means the row stands for "any country" /
  // "any campaign" fallback in org_uplift_coefficients land.
  const setGoalCell = (
    segKey: string,
    n: 1 | 2 | 3,
    value: string,
  ): void => {
    if (appId == null) return;
    const [country, _kw, campaign] = segKey.split("|");
    const num = Number(value);
    if (!Number.isFinite(num) || value.trim() === "") return;
    void upsertSegmentGoal.mutateAsync({
      app_id: appId,
      country: country === "" ? null : country,
      campaign: campaign === "" ? null : campaign,
      goal_n: n,
      target_value: num,
    });
  };

  // Column-header bulk: write `value` into goal_n for every visible
  // segment row. Fires upserts in parallel — for 36 rows × 1 col
  // this is one network round-trip-batch and TanStack invalidates
  // once at the end. Per-cell edits afterward override individual
  // rows.
  const bulkSetGoalColumn = async (
    segs: Array<{ country: string | null; campaign: string | null }>,
    n: 1 | 2 | 3,
    rawValue: string,
  ): Promise<void> => {
    if (appId == null) return;
    const num = Number(rawValue);
    if (!Number.isFinite(num)) return;
    await Promise.all(
      segs.map((s) =>
        upsertSegmentGoal.mutateAsync({
          app_id: appId,
          country: s.country,
          campaign: s.campaign,
          goal_n: n,
          target_value: num,
        }),
      ),
    );
  };

  // Default app: first one in the list once it loads.

  const onCsvChange = (text: string) => {
    setCsvText(text);
    setCsvError(null);
    if (!text.trim()) {
      setParsedRows([]);
      return;
    }
    try {
      setParsedRows(parseUpliftCsv(text, appId, dataKind));
    } catch (e) {
      setCsvError((e as Error).message);
      setParsedRows([]);
    }
  };

  const onUpload = async () => {
    if (parsedRows.length === 0) return;
    await upload.mutateAsync({ rows: parsedRows, replace: true });
    setCsvText("");
    setParsedRows([]);
  };

  const downloadExampleCsv = () => {
    const aid = appId ?? "<APP_ID>";
    let sample =
      "app_id,country,day,value,kind\n" +
      `${aid},US,${mainFrom},1.50,${dataKind}\n` +
      `${aid},US,${mainTo},2.20,${dataKind}\n`;
    for (const p of predicts) {
      sample +=
        `${aid},US,${p.from},${(1 + p.from / 10).toFixed(2)},${dataKind}\n` +
        `${aid},US,${p.to},${(2 + p.to / 10).toFixed(2)},${dataKind}\n`;
    }
    const blob = new Blob([sample], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "uplift_sample.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const generateExample = () => {
    // Local CSV with the user's chosen day-pairs filled with synthetic
    // ARPU/ARPPU growth — populates the textarea so the user can see
    // the shape before uploading.
    const days = Array.from(
      new Set([mainFrom, mainTo, ...predicts.flatMap((p) => [p.from, p.to])]),
    ).sort((a, b) => a - b);
    const rows: string[] = ["app_id,country,day,value,kind"];
    const countries = ["US", "GB", "DE", "CA", "FR"];
    for (const c of countries) {
      const base = 1 + Math.random() * 0.8;
      const growth = 1.25 + Math.random() * 0.15;
      days.forEach((d, idx) => {
        const v = (base * Math.pow(growth, idx)).toFixed(2);
        rows.push(`${appId ?? 1},${c},${d},${v},${dataKind}`);
      });
    }
    onCsvChange(rows.join("\n"));
  };

  const segments = useMemo(() => {
    type Seg = {
      country: string | null;
      keyword: string | null;
      campaign: string | null;
      values: Map<number, number>;
    };
    const grouped = new Map<string, Seg>();
    for (const r of dataQuery.data ?? []) {
      const key = [r.country ?? "", r.keyword ?? "", r.campaign ?? ""].join("|");
      let g = grouped.get(key);
      if (!g) {
        g = {
          country: r.country,
          keyword: r.keyword,
          campaign: r.campaign,
          values: new Map(),
        };
        grouped.set(key, g);
      }
      g.values.set(r.day, Number(r.value));
    }
    return Array.from(grouped.values());
  }, [dataQuery.data]);

  const allDays = useMemo(() => {
    const s = new Set<number>();
    for (const seg of segments) for (const d of seg.values.keys()) s.add(d);
    return Array.from(s).sort((a, b) => a - b);
  }, [segments]);

  // Look up the coefficient for one segment + day-pair.
  const getCoeff = (
    seg: { country: string | null; keyword: string | null; campaign: string | null },
    fromDay: number,
    toDay: number,
  ): string | null => {
    const m = (coeffsQuery.data ?? []).find(
      (c) =>
        c.country === seg.country &&
        c.keyword === seg.keyword &&
        c.campaign === seg.campaign &&
        c.from_day === fromDay &&
        c.to_day === toDay,
    );
    if (!m) return null;
    const n = Number(m.coefficient);
    return Number.isFinite(n) ? n.toFixed(2) : null;
  };

  const splitKW = splitBy === "Country + Keyword";
  const splitCamp = splitBy === "Campaign Name";

  // Day-pair list: between every two consecutive distinct days the
  // user has data for, we render an Uplift column.
  const upliftPairs: [number, number][] = [];
  for (let i = 1; i < allDays.length; i++) {
    upliftPairs.push([allDays[i - 1], allDays[i]]);
  }

  // ---- Stored-only mode ----
  // Some apps ship FINAL uplift coefficients directly (no raw ARPU/
  // ARPPU points — see migration 0053 for CTMOBI Universal Remote).
  // In that case the existing Setup / Upload / "Current data" surface
  // is misleading: there's nothing to upload, no per-day ARPU values
  // to display, just the country-or-campaign × day-pair coefficients.
  // We render a dedicated pivot table and hide every authoring widget.
  const storedRows = (coeffsQuery.data ?? []).filter(
    (c) => c.keyword === null,
  );
  const storedOnly = segments.length === 0 && storedRows.length > 0;

  const storedView = useMemo(() => {
    if (!storedOnly) return null;
    // Distinct (from, to) day pairs in canonical order.
    const pairSet = new Map<string, [number, number]>();
    for (const r of storedRows) {
      const k = `${r.from_day}-${r.to_day}`;
      if (!pairSet.has(k)) pairSet.set(k, [r.from_day, r.to_day]);
    }
    const pairs = Array.from(pairSet.values()).sort(
      (a, b) => a[0] - b[0] || a[1] - b[1],
    );

    // Group by (country, campaign). Country=null + campaign=null is
    // the "TOTAL" fallback row from the source spreadsheet. Sort:
    // (a) country=non-null first, alpha; (b) NULL-country fallback
    // last among country rows; (c) campaign-only rows after that.
    type Group = {
      country: string | null;
      campaign: string | null;
      coeffs: Map<string, number>;
    };
    const groups = new Map<string, Group>();
    for (const r of storedRows) {
      const k = `${r.country ?? ""}|${r.campaign ?? ""}`;
      let g = groups.get(k);
      if (!g) {
        g = { country: r.country, campaign: r.campaign, coeffs: new Map() };
        groups.set(k, g);
      }
      g.coeffs.set(`${r.from_day}-${r.to_day}`, Number(r.coefficient));
    }
    const sorted = Array.from(groups.values()).sort((a, b) => {
      const aHasC = a.country != null, bHasC = b.country != null;
      if (aHasC !== bHasC) return aHasC ? -1 : 1;
      if (aHasC && bHasC) return (a.country as string).localeCompare(b.country as string);
      // both country=null → put campaign rows after the fallback
      const aHasCamp = a.campaign != null, bHasCamp = b.campaign != null;
      if (aHasCamp !== bHasCamp) return aHasCamp ? 1 : -1;
      if (aHasCamp && bHasCamp) return (a.campaign as string).localeCompare(b.campaign as string);
      return 0;
    });

    return { pairs, groups: sorted };
  }, [storedOnly, storedRows]);

  return (
    <>
      <StSectionHead
        title="Uplift Coeff + Goals"
        desc="Train uplift coefficients on your historical revenue / ARPU data, then assign per-segment goals (CPA targets) used by autopilot."
      />

      {storedOnly && storedView && (
        <>
          <div className="card" style={{ marginBottom: 16, padding: 16 }}>
            <div className="field" style={{ maxWidth: 360, margin: 0 }}>
              <label className="label">App</label>
              <select
                className="select"
                value={appId ?? ""}
                onChange={(e) =>
                  setAppId(e.target.value ? Number(e.target.value) : null)
                }
              >
                <option value="">— pick app —</option>
                {(apps.data ?? []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name ?? a.bundle_id}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {appId != null && <GoalTargetPickerBlock appId={appId} />}
          {appId != null && <SegmentGoalsBlock appId={appId} />}

          <div className="card">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 16px",
                borderBottom: "1px solid var(--c-divider)",
              }}
            >
              <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>
                Uplift coefficients
              </h3>
              <span className="muted" style={{ fontSize: 12 }}>
                {storedView.groups.length} segments ·{" "}
                {storedRows.length} coefficients
              </span>
            </div>
            <div className="table-scroll">
              <table className="dt" style={{ minWidth: 980 }}>
                <thead>
                  <tr>
                    <th>Country</th>
                    <th>Campaign</th>
                    {storedView.pairs.map(([a, b]) => (
                      <th
                        key={`u-${a}-${b}`}
                        className="num"
                        style={{ background: "#f6f3ff" }}
                      >
                        Uplift
                        <br />
                        <span
                          className="muted"
                          style={{ fontWeight: 400, fontSize: 11 }}
                        >
                          D{a}→D{b}
                        </span>
                      </th>
                    ))}
                    {([1, 2, 3] as const).map((n) => (
                      <BulkGoalHeader
                        key={`g-${n}`}
                        n={n}
                        firstInGroup={n === 1}
                        busy={upsertSegmentGoal.isPending}
                        metric={goalMetric}
                        onApply={(raw) =>
                          bulkSetGoalColumn(
                            storedView.groups.map((g) => ({
                              country: g.country,
                              campaign: g.campaign,
                            })),
                            n,
                            raw,
                          )
                        }
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {storedView.groups.map((g, ri) => {
                    // segKey shape mirrors the legacy upload-driven
                    // view so future persistence (per-segment table)
                    // can index both code paths the same way.
                    // Stored-only rows have keyword=null always.
                    const segKey = [
                      g.country ?? "",
                      "",
                      g.campaign ?? "",
                    ].join("|");
                    const goalsForSeg = goalCells[segKey] ?? {};
                    return (
                      <tr key={ri}>
                        <td>
                          {g.country ?? (
                            <span className="muted">Default (any country)</span>
                          )}
                        </td>
                        <td className="mono" style={{ fontSize: 12 }}>
                          {g.campaign ?? (
                            <span className="muted">Any</span>
                          )}
                        </td>
                        {storedView.pairs.map(([a, b]) => {
                          const v = g.coeffs.get(`${a}-${b}`);
                          return (
                            <td
                              key={`u-${a}-${b}`}
                              className="num"
                              style={{ background: "#fbfaff" }}
                            >
                              {v != null ? `${v.toFixed(2)}×` : "—"}
                            </td>
                          );
                        })}
                        {([1, 2, 3] as const).map((n, idx) => (
                          <GoalCpaCell
                            key={`g-${n}`}
                            value={goalsForSeg[n] ?? ""}
                            onChange={(v) => setGoalCell(segKey, n, v)}
                            firstInGroup={idx === 0}
                            metric={goalMetric}
                          />
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div
              style={{
                padding: "12px 20px",
                borderTop: "1px solid var(--c-divider)",
                fontSize: 12,
                color: "var(--c-text-3)",
              }}
            >
              Pre-computed Uplift columns shipped with the app — read-only.
              Goal columns are per-segment CPA targets — edit by clicking
              the cell.
            </div>
          </div>
        </>
      )}

      {!storedOnly && (
      <>
      <div className="card" style={{ marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            cursor: "pointer",
            borderBottom: setupOpen ? "1px solid var(--c-divider)" : "none",
          }}
          onClick={() => setSetupOpen((o) => !o)}
        >
          <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>
            1 · Setup
          </h3>
          <span
            style={{
              color: "var(--c-text-3)",
              transform: setupOpen ? "rotate(90deg)" : "rotate(0)",
              transition: "transform .2s",
              display: "inline-flex",
            }}
          >
            {I.chevron}
          </span>
        </div>
        {setupOpen && (
          <div style={{ padding: 20 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 14,
              }}
            >
              <div className="field">
                <label className="label">App</label>
                <select
                  className="select"
                  value={appId ?? ""}
                  onChange={(e) =>
                    setAppId(e.target.value ? Number(e.target.value) : null)
                  }
                >
                  <option value="">— pick app —</option>
                  {(apps.data ?? []).map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name ?? a.bundle_id}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label className="label">Main target coefficient</label>
                <div
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  <span className="muted" style={{ fontSize: 12 }}>
                    From day
                  </span>
                  <input
                    className="input"
                    type="number"
                    min={0}
                    max={365}
                    style={{ width: 80 }}
                    value={mainFrom}
                    onChange={(e) =>
                      setMainFrom(Number(e.target.value) || 0)
                    }
                  />
                  <span className="muted" style={{ fontSize: 12 }}>
                    To day
                  </span>
                  <input
                    className="input"
                    type="number"
                    min={0}
                    max={365}
                    style={{ width: 80 }}
                    value={mainTo}
                    onChange={(e) => setMainTo(Number(e.target.value) || 0)}
                  />
                </div>
              </div>
              <div className="field">
                <label className="label">Split by</label>
                <select
                  className="select"
                  value={splitBy}
                  onChange={(e) =>
                    setSplitBy(
                      e.target.value as
                        | "Country"
                        | "Country + Keyword"
                        | "Campaign Name",
                    )
                  }
                >
                  <option>Country</option>
                  <option>Country + Keyword</option>
                  <option>Campaign Name</option>
                </select>
              </div>
              <div className="field">
                <label className="label">My data is</label>
                <select
                  className="select"
                  value={dataKind}
                  onChange={(e) =>
                    setDataKind(e.target.value as "ARPU" | "ARPPU")
                  }
                >
                  <option>ARPU</option>
                  <option>ARPPU</option>
                </select>
              </div>
              <div className="field" style={{ gridColumn: "1 / -1" }}>
                <label className="label">
                  Upload your data{" "}
                  <span className="muted" style={{ fontWeight: 400 }}>
                    (optional · adds extra day-pairs)
                  </span>
                </label>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  {predicts.map((p, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <span className="muted" style={{ fontSize: 12 }}>
                        From day
                      </span>
                      <input
                        className="input"
                        type="number"
                        min={0}
                        max={365}
                        style={{ width: 80 }}
                        value={p.from}
                        onChange={(e) => {
                          const ps = [...predicts];
                          ps[i] = { ...ps[i], from: Number(e.target.value) || 0 };
                          setPredicts(ps);
                        }}
                      />
                      <span className="muted" style={{ fontSize: 12 }}>
                        To day
                      </span>
                      <input
                        className="input"
                        type="number"
                        min={0}
                        max={365}
                        style={{ width: 80 }}
                        value={p.to}
                        onChange={(e) => {
                          const ps = [...predicts];
                          ps[i] = { ...ps[i], to: Number(e.target.value) || 0 };
                          setPredicts(ps);
                        }}
                      />
                      <button
                        type="button"
                        className="btn icon sm"
                        onClick={() =>
                          setPredicts(predicts.filter((_, j) => j !== i))
                        }
                        title="Remove"
                      >
                        {I.trash}
                      </button>
                    </div>
                  ))}
                  <Btn
                    icon={I.plus}
                    onClick={() =>
                      setPredicts([...predicts, { from: 90, to: 180 }])
                    }
                    style={{ alignSelf: "flex-start" }}
                  >
                    Add more data
                  </Btn>
                </div>
              </div>
            </div>
            <div className="field" style={{ marginTop: 14 }}>
              <label className="label">
                Paste CSV{" "}
                <span className="muted" style={{ fontWeight: 400 }}>
                  · header: <code>app_id,country,keyword,campaign,day,value,kind</code>
                </span>
              </label>
              <textarea
                className="input mono"
                rows={6}
                placeholder={`app_id,country,day,value,kind\n1,US,${mainFrom},1.50,${dataKind}\n1,US,${mainTo},2.20,${dataKind}`}
                value={csvText}
                onChange={(e) => onCsvChange(e.target.value)}
                style={{ fontSize: 12, padding: 12 }}
              />
              {csvError && (
                <div
                  style={{
                    color: "var(--c-neg-600)",
                    fontSize: 12,
                    marginTop: 6,
                  }}
                >
                  {csvError}
                </div>
              )}
              {parsedRows.length > 0 && (
                <div
                  className="muted"
                  style={{ fontSize: 12, marginTop: 6 }}
                >
                  Parsed {parsedRows.length} rows · Upload replaces existing
                  data for the same (app, kind) combination.
                </div>
              )}
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                marginTop: 18,
                flexWrap: "wrap",
              }}
            >
              <Btn icon={I.sparkles} onClick={generateExample}>
                Generate Example
              </Btn>
              <Btn icon={I.download} onClick={downloadExampleCsv}>
                Download Example CSV
              </Btn>
              <Btn
                variant="primary"
                icon={I.check}
                onClick={() => void onUpload()}
                disabled={parsedRows.length === 0 || upload.isPending}
              >
                Upload Data
              </Btn>
            </div>
          </div>
        )}
      </div>

      {appId != null && <GoalTargetPickerBlock appId={appId} />}
      {appId != null && <SegmentGoalsBlock appId={appId} />}

      {appId != null && segments.length > 0 && (
        <div className="card">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 16px",
              borderBottom: "1px solid var(--c-divider)",
            }}
          >
            <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>
              2 · Current data + coefficients
            </h3>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <span className="muted" style={{ fontSize: 12 }}>
                {segments.length} segments · {coeffsQuery.data?.length ?? 0}{" "}
                coefficients
              </span>
            </div>
          </div>
          <div className="table-scroll">
            <table className="dt" style={{ minWidth: 1100 }}>
              <thead>
                <tr>
                  <th>Country</th>
                  {splitKW && <th>Keyword</th>}
                  {splitCamp && <th>Campaign</th>}
                  {allDays.map((d) => (
                    <th key={`v-${d}`} className="num">
                      {dataKind}
                      <br />
                      <span
                        className="muted"
                        style={{ fontWeight: 400, fontSize: 11 }}
                      >
                        D{d}
                      </span>
                    </th>
                  ))}
                  {upliftPairs.map(([a, b]) => (
                    <th
                      key={`u-${a}-${b}`}
                      className="num"
                      style={{ background: "#f6f3ff" }}
                    >
                      Uplift
                      <br />
                      <span
                        className="muted"
                        style={{ fontWeight: 400, fontSize: 11 }}
                      >
                        D{a}→D{b}
                      </span>
                    </th>
                  ))}
                  {([1, 2, 3] as const).map((n) => (
                    <BulkGoalHeader
                      key={`g-${n}`}
                      n={n}
                      firstInGroup={n === 1}
                      busy={upsertSegmentGoal.isPending}
                      metric={goalMetric}
                      onApply={(raw) =>
                        bulkSetGoalColumn(
                          segments.map((s) => ({
                            country: s.country,
                            // Legacy view shows keyword-level rows
                            // but org_app_segment_goals stores at
                            // (country, campaign) granularity — the
                            // bulk write here collapses keywords;
                            // per-cell edits stay per-row in the UI
                            // (last-write-wins on the same (country,
                            // campaign) key).
                            campaign: s.campaign,
                          })),
                          n,
                          raw,
                        )
                      }
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {segments.map((seg, ri) => {
                  const segKey = [
                    seg.country ?? "",
                    seg.keyword ?? "",
                    seg.campaign ?? "",
                  ].join("|");
                  const goalsForSeg = goalCells[segKey] ?? {};
                  return (
                    <tr key={ri}>
                      <td>{seg.country ?? "—"}</td>
                      {splitKW && (
                        <td className="mono" style={{ fontSize: 12 }}>
                          {seg.keyword ?? "—"}
                        </td>
                      )}
                      {splitCamp && <td>{seg.campaign ?? "—"}</td>}
                      {allDays.map((d) => (
                        <td key={`v-${d}`} className="num">
                          {seg.values.get(d)?.toFixed(2) ?? "—"}
                        </td>
                      ))}
                      {upliftPairs.map(([a, b]) => {
                        const c = getCoeff(seg, a, b);
                        return (
                          <td
                            key={`u-${a}-${b}`}
                            className="num"
                            style={{
                              background: "#fbfaff",
                              color: "var(--c-text-3)",
                            }}
                          >
                            {c ? `${c}×` : "—"}
                          </td>
                        );
                      })}
                      {([1, 2, 3] as const).map((n, idx) => (
                        <GoalCpaCell
                          key={`g-${n}`}
                          value={goalsForSeg[n] ?? ""}
                          onChange={(v) => setGoalCell(segKey, n, v)}
                          firstInGroup={idx === 0}
                          metric={goalMetric}
                        />
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div
            style={{
              padding: "12px 20px",
              borderTop: "1px solid var(--c-divider)",
              fontSize: 12,
              color: "var(--c-text-3)",
              display: "flex",
              alignItems: "center",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: "#f6f3ff",
                }}
              />{" "}
              Uplift columns auto-computed by backend (read-only)
            </span>
            <span>·</span>
            <span>
              Goal columns are per-app CPA targets — change them in Rules
              for Goals or right here in the cell
            </span>
          </div>
        </div>
      )}

      {appId != null && segments.length === 0 && (
        <div
          className="card card-pad"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 180,
            flexDirection: "column",
            gap: 8,
          }}
        >
          <div className="muted" style={{ fontSize: 13 }}>
            No data yet for this app.
          </div>
          <div className="muted" style={{ fontSize: 12 }}>
            Paste CSV above and click Upload to bootstrap the table.
          </div>
        </div>
      )}
      </>
      )}
    </>
  );
}

function GoalCpaCell({
  value,
  onChange,
  firstInGroup,
  metric,
}: {
  value: string;
  onChange: (next: string) => void;
  firstInGroup: boolean;
  // Drives the leading symbol: '$' for ARPU/ARPPU (money values),
  // no symbol for ROAS (it's a multiplier, not a currency amount).
  // Undefined = goal_target not configured yet, render bare.
  metric?: GoalMetric;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  const commit = () => {
    if (draft !== value) onChange(draft);
    setEditing(false);
  };

  return (
    <td
      className="num"
      onClick={() => !editing && setEditing(true)}
      style={{
        cursor: "pointer",
        background: editing ? "var(--c-accent-50)" : undefined,
        borderLeft: firstInGroup ? "1px solid var(--c-divider)" : undefined,
      }}
    >
      {editing ? (
        <input
          className="input mono"
          autoFocus
          type="number"
          step="0.01"
          min="0"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") {
              setDraft(value);
              setEditing(false);
            }
          }}
          style={{
            width: 80,
            height: 24,
            fontSize: 12,
            textAlign: "right",
          }}
        />
      ) : (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          {metric === "ARPU" || metric === "ARPPU" ? (
            <span className="muted">$</span>
          ) : null}
          {value ? Number(value).toFixed(2) : "—"}
          <span style={{ opacity: 0.3, marginLeft: 2, fontSize: 10 }}>
            ✎
          </span>
        </span>
      )}
    </td>
  );
}

/** Parse the user's CSV. Required columns: app_id, day, value, kind.
 *  Optional: country, keyword, campaign. Header row required.
 *  `fallbackKind` lets the user omit the `kind` column when they've
 *  picked one in the Setup form's "My data is" select. */
function parseUpliftCsv(
  text: string,
  fallbackAppId: number | null,
  fallbackKind?: "ARPU" | "ARPPU",
): UpliftUploadRow[] {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length < 2) {
    throw new Error("CSV needs a header row + at least one data row");
  }
  const header = lines[0].split(",").map((h) => h.trim().toLowerCase());
  const idx = (col: string) => header.indexOf(col);
  const REQUIRED = fallbackKind ? ["day", "value"] : ["day", "value", "kind"];
  for (const col of REQUIRED) {
    if (idx(col) < 0) throw new Error(`Missing required column: ${col}`);
  }
  const out: UpliftUploadRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(",").map((c) => c.trim());
    const appIdRaw = idx("app_id") >= 0 ? cols[idx("app_id")] : "";
    const app_id = appIdRaw ? Number(appIdRaw) : fallbackAppId;
    if (app_id == null) {
      throw new Error(`Row ${i + 1}: app_id missing and no app picked`);
    }
    const day = Number(cols[idx("day")]);
    const value = cols[idx("value")];
    const kindRaw =
      idx("kind") >= 0
        ? cols[idx("kind")].toUpperCase()
        : (fallbackKind ?? "ARPU");
    if (kindRaw !== "ARPU" && kindRaw !== "ARPPU") {
      throw new Error(`Row ${i + 1}: kind must be ARPU or ARPPU`);
    }
    out.push({
      app_id,
      country: idx("country") >= 0 ? cols[idx("country")] || null : null,
      keyword: idx("keyword") >= 0 ? cols[idx("keyword")] || null : null,
      campaign: idx("campaign") >= 0 ? cols[idx("campaign")] || null : null,
      day,
      value,
      kind: kindRaw,
    });
  }
  return out;
}

// ---------------------------------------------------------------------------
// 6.2 Rules for Goals
// ---------------------------------------------------------------------------

const PCT_OPTIONS = [10, 15, 20, 25, 30, 35, 40, 45, 50];

function GoalRulesSection() {
  const apps = useApps();
  const [appId, setAppId] = useStickyAppId(apps.data);
  const goals = useGoalRules(appId);
  const update = useUpdateGoalRule();
  // `main_event` has no functional consumer anywhere: the engine reads only the
  // bid caps + row existence from org_goal_rules (both the detection-ON predict
  // path and the legacy Adapty path). The event gate lives entirely in the rule
  // condition (custom_metric / event ≥ N), and there's no min-count here
  // (dropped in migration 0058). So the Main-event picker is dead + misleading
  // for EVERY app — dropped from the UI. A preset is "configured" once its caps
  // are applied.

  const rows = goals.data ?? [];

  return (
    <>
      <StSectionHead
        title="Rules for Goals"
        desc="For each goal, set the maximum bid moves autopilot is allowed to make. (The event/threshold gate lives in the rule condition; the target values live in Coefficients & Goals.)"
        right={
          <select
            className="select"
            value={appId ?? ""}
            onChange={(e) =>
              setAppId(e.target.value ? Number(e.target.value) : null)
            }
            style={{ minWidth: 200 }}
          >
            <option value="">— pick app —</option>
            {(apps.data ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name ?? a.bundle_id}
              </option>
            ))}
          </select>
        }
      />

      {appId == null && (
        <div className="card card-pad muted" style={{ textAlign: "center" }}>
          Pick an app to configure goal rules.
        </div>
      )}

      {appId != null && (
        <div className="stack stack-16">
          {rows.map((r) => {
            const configured = r.configured_at != null;
            return (
              <div
                key={r.id}
                className="card"
                style={{
                  background: configured
                    ? "linear-gradient(180deg, #ecfaf0, #fff)"
                    : "linear-gradient(180deg, #fff8e6, #fff)",
                  border: configured
                    ? "1px solid #b9e6c5"
                    : "1px solid #f0d894",
                }}
              >
                <div
                  style={{
                    padding: "14px 20px",
                    display: "grid",
                    // status / Bid+ / Bid− / Apply. Main event dropped from the
                    // UI (unused by the engine — the event gate is the rule
                    // condition). Min events removed in migration 0058.
                    gridTemplateColumns: "110px 150px 150px 100px",
                    gap: 14,
                    alignItems: "end",
                  }}
                >
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      Goal {r.goal_n}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--c-text-3)",
                        marginTop: 2,
                      }}
                    >
                      {configured ? "Configured" : "Not configured yet"}
                    </div>
                  </div>
                  <div className="field" style={{ minWidth: 0 }}>
                    <label className="label">Bid increase max</label>
                    <select
                      className="select"
                      value={r.bid_increase_max_pct}
                      onChange={(e) =>
                        void update.mutateAsync({
                          app_id: appId,
                          goal_n: r.goal_n,
                          patch: { bid_increase_max_pct: Number(e.target.value) },
                        })
                      }
                    >
                      {PCT_OPTIONS.map((p) => (
                        <option key={p} value={p}>
                          +{p}%
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="field">
                    <label className="label">Bid decrease max</label>
                    <select
                      className="select"
                      value={r.bid_decrease_max_pct}
                      onChange={(e) =>
                        void update.mutateAsync({
                          app_id: appId,
                          goal_n: r.goal_n,
                          patch: { bid_decrease_max_pct: Number(e.target.value) },
                        })
                      }
                    >
                      {PCT_OPTIONS.map((p) => (
                        <option key={p} value={p}>
                          −{p}%
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    {configured ? (
                      <Btn
                        icon={I.edit}
                        onClick={() =>
                          void update.mutateAsync({
                            app_id: appId,
                            goal_n: r.goal_n,
                            patch: { configured: false },
                          })
                        }
                      >
                        Edit
                      </Btn>
                    ) : (
                      <Btn
                        variant="primary"
                        onClick={() =>
                          void update.mutateAsync({
                            app_id: appId,
                            goal_n: r.goal_n,
                            patch: { configured: true },
                          })
                        }
                      >
                        Apply
                      </Btn>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// 6.3 Rules for New Campaigns — placeholder
// ---------------------------------------------------------------------------

function NewCampaignsPlaceholder() {
  return (
    <>
      <StSectionHead
        title="Rules for New Campaigns"
        desc="Apply default rules and goal mappings to new campaigns automatically as soon as they are detected."
      />
      <div
        className="card card-pad"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 240,
          flexDirection: "column",
          gap: 10,
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            background: "var(--c-surface-sunk)",
            display: "grid",
            placeItems: "center",
            color: "var(--c-text-3)",
          }}
        >
          {I.compass}
        </div>
        <div style={{ fontSize: 13.5, fontWeight: 500 }}>Available in v1.2</div>
        <div className="muted" style={{ fontSize: 12.5 }}>
          We&apos;re still designing the rule templates for new campaigns.
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Shared app picker (used by Cohort events config)
// ---------------------------------------------------------------------------

function AppPickerCard({
  appId,
  setAppId,
}: {
  appId: number | null;
  setAppId: (id: number | null) => void;
}) {
  const apps = useApps();
  useEffect(() => {
    if (appId === null && apps.data && apps.data.length > 0) {
      setAppId(apps.data[0].id);
    }
  }, [apps.data, appId, setAppId]);
  return (
    <div className="card" style={{ marginBottom: 16, padding: 16 }}>
      <div className="field" style={{ maxWidth: 360, margin: 0 }}>
        <label className="label">App</label>
        <select
          className="select"
          value={appId ?? ""}
          onChange={(e) => setAppId(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">— pick app —</option>
          {(apps.data ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              {a.name ?? a.bundle_id}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-app cohort source config
// ---------------------------------------------------------------------------

function CohortConfigSection() {
  const apps = useApps();
  const [appId, setAppId] = useStickyAppId(apps.data);
  const cfgQuery = useAppCohortConfig(appId);
  const upsert = useUpsertAppCohortConfig();

  const [source, setSource] = useState<CohortSource>("adapty");
  const [events, setEvents] = useState<string[]>(ADJUST_EVENT_OPTIONS);
  const [msg, setMsg] = useState<string | null>(null);

  const cfg = cfgQuery.data ?? null;
  useEffect(() => {
    setSource(cfg?.source ?? "adapty");
    setEvents(
      cfg?.source === "adjust" && cfg.events.length > 0
        ? cfg.events
        : ADJUST_EVENT_OPTIONS,
    );
    setMsg(null);
  }, [cfg?.app_id, appId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (ev: string) =>
    setEvents((prev) =>
      prev.includes(ev) ? prev.filter((e) => e !== ev) : [...prev, ev],
    );

  const onSave = async () => {
    if (appId == null) return;
    try {
      await upsert.mutateAsync({
        app_id: appId,
        source,
        events: source === "adjust" ? events : [],
      });
      setMsg("Saved.");
    } catch (e) {
      setMsg(`Save failed: ${(e as Error).message}`);
    }
  };

  return (
    <>
      <StSectionHead
        title="Cohort events configuration"
        desc="Choose which provider's cohort events this app shows across Overview / Ads Manager. Default is Adapty. Switch to Adjust for apps tracked via the Adjust → GCS pipeline."
      />
      <AppPickerCard appId={appId} setAppId={setAppId} />
      {appId != null && (
        <div className="card" style={{ padding: 18 }}>
          <div className="field" style={{ maxWidth: 420 }}>
            <label className="label">Cohort source</label>
            <div style={{ display: "flex", gap: 18, marginTop: 4 }}>
              <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input
                  type="radio"
                  name="cohort-source"
                  checked={source === "adapty"}
                  onChange={() => setSource("adapty")}
                />
                Adapty (default)
              </label>
              <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input
                  type="radio"
                  name="cohort-source"
                  checked={source === "adjust"}
                  onChange={() => setSource("adjust")}
                />
                Adjust
              </label>
            </div>
          </div>

          {source === "adjust" && (
            <div className="field">
              <label className="label">Adjust events to track</label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 6,
                  marginTop: 4,
                }}
              >
                {ADJUST_EVENT_OPTIONS.map((ev) => (
                  <label
                    key={ev}
                    style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}
                  >
                    <input
                      type="checkbox"
                      checked={events.includes(ev)}
                      onChange={() => toggle(ev)}
                    />
                    {ev}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
            <Btn
              variant="primary"
              disabled={upsert.isPending}
              onClick={() => void onSave()}
            >
              {upsert.isPending ? "Saving…" : "Save"}
            </Btn>
          </div>
          {msg && (
            <div className="muted" style={{ marginTop: 10, fontSize: 13 }}>
              {msg}
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Per-app Overview tile config (§36)
// ---------------------------------------------------------------------------

function OverviewTilesSection() {
  const apps = useApps();
  const [appId, setAppId] = useStickyAppId(apps.data);
  return (
    <>
      <StSectionHead
        title="Overview tiles"
        desc="Pick which events appear as KPI tiles on the Overview page for each app. Defaults — when nothing is configured — are Spend / Revenue / ROAS / Installs / Trials / CPA, same as today."
      />
      <AppPickerCard appId={appId} setAppId={setAppId} />
      {appId != null && <OverviewTilesEditor appId={appId} />}
    </>
  );
}
